import { useQuery } from '@tanstack/react-query';
import { isNormalizedApiError } from '../app/api-client';
import { navContextFor, type NavContext } from '../app/access-model';
import { listImspRegistrars } from '../features/cross-imsp/api';
import { useActiveRegistrar } from './use-active-registrar';
import { useSession } from './use-session';

export interface NavContextState {
  /** `undefined` while it is not known; both readers then fail closed. */
  context: NavContext | undefined;
  /** The bff is being asked; a reader that would refuse on `undefined` waits instead. */
  pending: boolean;
}

/**
 * The access context of the Registrar being worked on, for the menu and the
 * route guard alike (docs/shape-ops-tenant-selector.md §7.2).
 *
 * Where the session cannot say whether the active Registrar is an IMSP (several
 * Registrars, an IMSP among their kinds), the bff is asked the way the stock
 * check page asks it: UC92-03's list of IMSP Registrars is answered to a
 * Registrar role exactly when the policy admits its active Registrar to UC92,
 * and refused (`NOT_IMSP_REGISTRAR`) when it does not. Any other failure leaves
 * the context unknown, which hides the entry.
 */
export function useNavContext(): NavContextState {
  const session = useSession().data;
  const { idno } = useActiveRegistrar();
  const fromSession = navContextFor(session ?? undefined, idno);

  const asked = useQuery({
    queryKey: ['active-registrar-imsp', idno],
    queryFn: async (): Promise<boolean> => {
      try {
        await listImspRegistrars();
        return true;
      } catch (error) {
        if (isNormalizedApiError(error) && error.status === 403) return false;
        throw error;
      }
    },
    enabled: fromSession === undefined,
    staleTime: Infinity,
    retry: false,
  });

  if (fromSession) return { context: fromSession, pending: false };
  if (asked.data !== undefined) return { context: { registrarIsImsp: asked.data }, pending: false };
  return { context: undefined, pending: asked.isPending };
}
